import type { Me, VisibleArea } from '@portal/contracts';
import { BrandLogo } from '@portal/ui';
import { LogOut, Menu, Moon, Search, Sun } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api.js';
import { onLinkClick } from '../router.js';
import type { Theme } from '../theme.js';

interface Props {
  me: Me;
  areas: VisibleArea[];
  activeArea: string | null;
  theme: Theme;
  onToggleTheme(): void;
}

/**
 * Anwendungskopf (DESIGN.md § 11/4.1): offizielles Logo (kompakt) links, horizontale Navigation der sichtbaren Bereiche,
 * rechts Suche und beschrifteter Umschalter Hell/Dunkel. Unter 650 px: beschriftetes Menü.
 * Bereiche kommen ausschließlich aus /api/modules (serverseitig vom PDP gefiltert).
 */
export function Header({ me, areas, activeArea, theme, onToggleTheme }: Props) {
  const [open, setOpen] = useState(false);
  return (
    <header className="sh-head">
      <a href="/" className="sh-brand" onClick={onLinkClick('/')} aria-label="Arbeitsplatz">
        <BrandLogo klein />
      </a>
      <button type="button" className="sh-menu pb-button pb-button--quiet" aria-expanded={open} aria-controls="sh-nav" onClick={() => setOpen(!open)}>
        <Menu size={16} aria-hidden /> Menü
      </button>
      <nav id="sh-nav" className={`sh-nav${open ? ' is-open' : ''}`} aria-label="Hauptnavigation">
        {areas.map((a) => (
          <a
            key={a.key}
            href={a.path}
            className={`sh-nav__item${a.key === activeArea ? ' is-active' : ''}`}
            aria-current={a.key === activeArea ? 'page' : undefined}
            onClick={(e) => {
              setOpen(false);
              onLinkClick(a.path)(e);
            }}
          >
            {a.label}
          </a>
        ))}
      </nav>
      <div className="sh-tools">
        <label className="sh-search">
          <Search size={15} aria-hidden />
          <span className="sr-only">Suche</span>
          <input type="search" placeholder="Suche" disabled title="Suche folgt mit den Fachmodulen" />
        </label>
        <button type="button" className="sh-theme pb-button pb-button--quiet" onClick={onToggleTheme} aria-label={`Ansicht: ${theme === 'light' ? 'Hell' : 'Dunkel'} — umschalten`}>
          {theme === 'light' ? <Sun size={15} aria-hidden /> : <Moon size={15} aria-hidden />}
          {theme === 'light' ? 'Hell' : 'Dunkel'}
        </button>
        <button type="button" className="sh-user pb-button pb-button--quiet" onClick={() => void api.logout()} title={`Abmelden (${me.displayName})`}>
          <span className="sh-user__name">{me.displayName}</span>
          <LogOut size={15} aria-hidden />
          <span className="sr-only">Abmelden</span>
        </button>
      </div>
    </header>
  );
}

export function SubNav({ area, activeModule }: { area: VisibleArea; activeModule: string | null }) {
  if (area.modules.length < 2) return null;
  return (
    <nav className="sh-sub" aria-label={`Bereich ${area.label}`}>
      {area.modules.map((m) => (
        <a key={m.key} href={m.path} onClick={onLinkClick(m.path)} className={m.key === activeModule ? 'is-active' : ''} aria-current={m.key === activeModule ? 'page' : undefined}>
          {m.label}
        </a>
      ))}
    </nav>
  );
}
